import { useEffect, useRef, useState } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { Alerte, Bouton, Carte } from '../ui';

/**
 * Confirmation d'adresse e-mail, depuis le lien reçu par courriel.
 *
 * Rien à saisir : le jeton de l'URL suffit, la confirmation part donc à l'ouverture de
 * l'écran. Faire cliquer sur un bouton « Confirmer » quelqu'un qui vient de cliquer sur un
 * lien de confirmation lui demanderait deux fois la même chose.
 *
 * `skipRefresh` parce que la confirmation ne suppose pas de session : l'écran d'inscription
 * annonce qu'on peut se connecter sans attendre, le lien est donc souvent ouvert ailleurs —
 * autre navigateur, téléphone — que là où le compte a été créé.
 */
export function VerifierEmail({
  jeton,
  surConnexion,
}: {
  jeton: string;
  surConnexion?: () => void;
}) {
  const { client } = useSession();
  const [etat, setEtat] = useState<'envoi' | 'confirme' | 'echec'>('envoi');
  const [erreur, setErreur] = useState<string | null>(null);
  // Le jeton ne vaut qu'une fois — le serveur le consomme (`consumeUserToken`). Un second
  // envoi, ne serait-ce que le double montage de StrictMode en développement, répondrait
  // « Lien invalide ou déjà utilisé » sur une confirmation pourtant réussie.
  const tentee = useRef(false);

  useEffect(() => {
    if (jeton === '' || tentee.current) return;
    tentee.current = true;
    void client
      .request('/auth/verify-email', { method: 'POST', body: { token: jeton }, skipRefresh: true })
      .then(() => setEtat('confirme'))
      .catch((cause: unknown) => {
        setErreur(messageDErreur(cause));
        setEtat('echec');
      });
  }, [jeton, client]);

  // Un lien sans jeton — recopié à la main, tronqué par un client de messagerie — le dit
  // tout de suite plutôt que de laisser tourner une confirmation qui n'a jamais commencé.
  if (jeton === '') {
    return (
      <Carte titre="Ce lien est incomplet">
        <p style={{ marginTop: 0 }}>
          Ouvrez-le directement depuis l'e-mail reçu, sans le recopier : certains clients de
          messagerie le coupent en deux.
        </p>
        <Bouton variante="secondaire" onClick={() => surConnexion?.()}>
          Revenir à la connexion
        </Bouton>
      </Carte>
    );
  }

  if (etat === 'confirme') {
    return (
      <Carte titre="Votre adresse est confirmée">
        <p style={{ marginTop: 0 }}>
          Rien d'autre à faire : vous pouvez vous connecter et retrouver votre dossier.
        </p>
        <Bouton onClick={() => surConnexion?.()}>Se connecter</Bouton>
      </Carte>
    );
  }

  if (etat === 'echec') {
    return (
      <Carte titre="Ce lien n’a pas fonctionné">
        <Alerte titre="L’adresse n’a pas été confirmée">{erreur}</Alerte>
        {/* Un lien périmé ou déjà ouvert n'est pas un compte perdu : la confirmation n'a
            jamais bloqué la connexion, et le dire évite de faire recréer un compte. */}
        <p>
          Un lien de confirmation expire, et ne sert qu'une fois. Votre compte existe toujours :
          connectez-vous normalement, un nouveau message vous sera envoyé si besoin.
        </p>
        <Bouton onClick={() => surConnexion?.()}>Se connecter</Bouton>
      </Carte>
    );
  }

  return (
    <Carte titre="Confirmation en cours">
      <p style={{ marginTop: 0 }}>Un instant, nous confirmons votre adresse…</p>
    </Carte>
  );
}
